/**
 * Adapter layer that maps GraphQL response types into flat Device view-model
 * types consumed by rescue portal Devices page components.
 *
 * The `rescueDevices` query returns inline transfer details, so no separate
 * enrichment step is needed — a single mapper handles everything.
 */

import { DateTime } from 'luxon';
import type { Device } from './types';
import type { RescueDeviceItemGql, RescueDeviceFilter } from './rescueQueries';

// ── Helpers ──────────────────────────────────

function toIsoString(dt: string | null | undefined): string {
  if (!dt) return '';
  const parsed = DateTime.fromISO(dt);
  return parsed.isValid ? parsed.toISO()! : '';
}

// ── Map RescueDeviceItem to Device view-model ──

/**
 * Maps a `RescueDeviceItem` (pet + inline transfer) from the `rescueDevices`
 * query into a flat `Device` view-model.
 *
 * The `filter` determines holderType and transferStatus. Transfer details
 * (adopter name/email, foster holder name) come from the inline `transfer`
 * field — no N+1 enrichment needed.
 */
export function mapRescueDeviceItemToDevice(
  item: RescueDeviceItemGql,
  resellerName: string,
  filter: RescueDeviceFilter,
): Device | null {
  const pet = item.pet;
  const device = pet.device;
  if (!device) {
    return null; // shouldn't happen — backend only returns device-bearing pets
  }

  const transfer = item.transfer;

  // Defaults (IN_RESCUE)
  let holderType: Device['holderType'] = 'rescue';
  let holderName = resellerName;
  let holderEmail: string | null = null;
  let holderPhone: string | null = null;
  let transferStatus: Device['transferStatus'] = null;
  let adopterName: string | null = null;
  let adopterEmail: string | null = null;
  let adopterPhone: string | null = null;
  let transferDate: string | null = null;
  let completedDate: string | null = null;

  if (filter === 'IN_FOSTER' && transfer) {
    holderType = 'foster';
    holderName = transfer.toFullName || resellerName;
    holderEmail = transfer.toEmail || null;
    holderPhone = transfer.toPhone || null;
  } else if (filter === 'PENDING_ADOPTIONS' && transfer) {
    transferStatus = 'pending_adopter';
    adopterName = transfer.toFullName || null;
    adopterEmail = transfer.toEmail || null;
    adopterPhone = transfer.toPhone || null;
    transferDate = toIsoString(transfer.createdAt);
  } else if (filter === 'COMPLETED_ADOPTIONS' && transfer) {
    transferStatus = 'completed';
    adopterName = transfer.toFullName || null;
    adopterEmail = transfer.toEmail || null;
    adopterPhone = transfer.toPhone || null;
    transferDate = toIsoString(transfer.createdAt);
    completedDate = toIsoString(transfer.completedAt ?? transfer.updatedAt);
  }

  return {
    _petGraphqlId: pet.id,
    _deviceGraphqlId: device.id,
    id: device.moduleId,
    type: device.currentCollarModelLongName || 'Unknown Fi Device',
    petName: pet.name || null,
    chip: pet.chip
      ? {
          id: pet.chip.id,
          shortId: pet.chip.shortId,
          serialNumber: pet.chip.serialNumber,
        }
      : null,
    holderType,
    holderName,
    holderEmail,
    holderPhone,
    battery: item.batteryPercent,
    status: item.isOnline ? 'online' : 'offline',
    lastSeen: toIsoString(device.lastConnectionState?.date),
    assignedDate: null,
    subscription: device.hasActiveSubscription ? 'rescue' : 'inactive',
    safeZone: item.hasSafeZone,
    wifiConfigured: item.hasWifiSetup,
    transferStatus,
    adopterName,
    adopterEmail,
    adopterPhone,
    transferDate,
    completedDate,
  };
}
